import React, { FC, MouseEvent, MouseEventHandler, useEffect, useMemo, useState } from 'react';
import { 
  getLayer, IMeeting, isCanEditMeeting, IUpdatedValuesWhenDragging, 
  TModifyRecurringType, useViewModel, zIndexCurrentTimeLine 
} from '@utils';
import { CalendarViewModel } from '@views';
import { set } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { toast } from 'react-toastify';
import { MeetingViewModel } from '../calendar';
import { DndBlock } from '../DndBlock';
import { MessageDialogViewModel } from '../messageDialog';
import { Info } from './components/Info';
import { getActualMeeting, getLeft, getTitle, onClickMeeting } from './utils';
import '../../styles/components/meeting.scss';

interface IMeetingProps {
  meeting: IMeeting;
  isMeetingPlaner?: boolean;
  isShowInitiator?: boolean;
  colWidth: number;
  offsetLeft: number;
  pageLeft: number;
  isDayCalendar?: boolean;
  isCreatingMeeting?: boolean;
}

/**
 * @meeting объект встречи
 * @isMeetingPlaner булево, показывающая, что эта встреча будет отображена в планировщике. При значении true добавляется occupied-time элемент и не показывается окно детального просмотра при клике
 * @isShowInitiator булево, отвечающее за отображение организатора под названием встречи. Отображается при просмотре КФЗ
 * @colWidth ширина колонки. Нужна для расчета ширины встречи
 * @offsetLeft left от колонки со временем
 * @pageLeft left от экрана
 * @isDayCalendar если true, то встречи в дневном календаре и будут расстягиваться на всю ширину
 */
export const Meeting: FC<IMeetingProps> = observer(({
  meeting, isMeetingPlaner, isShowInitiator, colWidth, offsetLeft, pageLeft, isDayCalendar, isCreatingMeeting
}) => {
  const meetingViewModel = useViewModel(MeetingViewModel);
  const calendarViewModel = useViewModel(CalendarViewModel);
  const messageDialogService = useViewModel(MessageDialogViewModel);

  const [isMeetingInPast, setIsMeetingInPast] = useState(false);
  const isDraggingMeeting = useMemo(() => (
    !!meeting?.draggingValues && meetingViewModel?.draggingMeetingId === meeting.id
  ), [!!meeting?.draggingValues, meetingViewModel?.draggingMeetingId]);

  const actualValues: IUpdatedValuesWhenDragging = useMemo(() => ({
    startDate: isDraggingMeeting ? meeting.draggingValues.startDate : meeting.startDate,
    endDate: isDraggingMeeting ? meeting.draggingValues.endDate : meeting.endDate,
    layer: isDraggingMeeting ? meeting.draggingValues.layer : meeting.layer,
  }), [
    isDraggingMeeting, meeting?.draggingValues?.startDate, 
    meeting?.draggingValues?.endDate, meeting?.draggingValues?.layer, 
    meeting.startDate, meeting.endDate
  ]);

  const actualLayer = useMemo(() => (
    isDraggingMeeting ? 0 : actualValues.layer
  ), [isDraggingMeeting, actualValues.layer]);

  const width = useMemo(() => (
    colWidth - (actualLayer * (10 * colWidth / 100)) - (10 * colWidth / 100)
  ), [colWidth, actualValues.layer]);
  
  const [zIndex, setZIndex] = useState(meeting.layer + 1);
  const [left, setLeft] = useState(
    getLeft(
      offsetLeft, isDayCalendar, colWidth, 
      (meeting.startDate.getDay() === 0 ? 6 : meeting.startDate.getDay() - 1), 
      actualLayer,
    )
  );

  useEffect(() => {
    setLeft(getLeft(
      offsetLeft, isDayCalendar, colWidth, 
      (actualValues.startDate.getDay() === 0 ? 6 : actualValues.startDate.getDay() - 1), 
      actualLayer
    ));
  }, [
    calendarViewModel.viewCalendar.length, offsetLeft, isDraggingMeeting, 
    actualValues.layer, actualValues.startDate.getDay()
  ]);

  useEffect(() => {
    isDraggingMeeting && setZIndex(100);
  }, [isDraggingMeeting]);

  const title = useMemo(() =>(
    getTitle(meeting, isShowInitiator)
  ), [
    meeting.meetingName, meeting.startDate, meeting.endDate, 
    isShowInitiator, meeting?.draggingValues
  ]);

  const clickMeetingHandler: MouseEventHandler<HTMLDivElement> = (e) =>
    onClickMeeting(e, false, meetingViewModel.setPopupCoordinates, meeting, meetingViewModel.setMoreMeetingInformation);

  let timer: NodeJS.Timeout;
  const onMoveMouse: MouseEventHandler<HTMLDivElement> = (e) => {
    if (e.type === 'mouseenter') timer = setTimeout(() => setZIndex(zIndexCurrentTimeLine - 1), 400);
    else {
      setZIndex(isDraggingMeeting ? 100 : meeting.layer + 1);
      clearTimeout(timer);
    }
  };

  const onChangePosition = (_startDate: Date, _endDate: Date, e: any) => {
    setIsMeetingInPast(false);
    // условие сработает, если мы только начали двигать встречу (до этого она не была в режиме редактирования). 
    // Тогда ей добавляются начальные значения в draggedValues
    if (!meeting?.draggingValues) {
      const startValues: IUpdatedValuesWhenDragging = {
        startDate: meeting.startDate,
        endDate: meeting.endDate,
        layer: meeting.layer
      };
      calendarViewModel.setMeetingDragging(meeting.id, startValues, meeting.startDate);
    }

    // значение left в зависимости от того, где находится курсор, позиция делится на кол-во дней
    const newColLeft = Math.floor((e.clientX - pageLeft) / colWidth);

    // если курсор не выходит за левую и правую границы (за пределы .cols)
    e.clientX > pageLeft
      && e.clientX < colWidth * 7 + offsetLeft
      && setLeft(getLeft(offsetLeft, isDayCalendar, colWidth, newColLeft, actualLayer));
  };

  const isCanMove = useMemo(() =>
    ((isCanEditMeeting(meeting) && !meetingViewModel.draggingMeetingId)
      || (meetingViewModel.draggingMeetingId === meeting.id && !!meeting.draggingValues)
    ) && !isMeetingPlaner
    , [isDraggingMeeting, meetingViewModel.draggingMeetingId, isMeetingPlaner]);

  const onCancelDragging = (e?: MouseEvent<HTMLDivElement>) => {
    e?.stopPropagation();
    setIsMeetingInPast(true);
    calendarViewModel.setMeetingDragging(meeting.id, false, meeting.startDate);
    meetingViewModel.setMeetingDraggingId(null);
  };

  const onAcceptDragging = () => {
    const actualMeeting = getActualMeeting(meeting);

    // если встреча цикличная
    if (meeting.intervalType !== 'WITHOUT_REPEAT') {
      // функция, сработающая при "Сохранить" в модалке циклич встреч
      const handleChange = (modifyRecurringMeetingType: TModifyRecurringType) => {
        const updatedMeeting = { ...actualMeeting, modifyRecurringMeetingType };
        calendarViewModel.moveMeetingToOtherDay(actualMeeting, meeting.startDate);
        calendarViewModel.setMeetingDragging(meeting.id, false, meeting.startDate);
        meetingViewModel.setMeetingDraggingId(null);

        return meetingViewModel.updateMeeting(updatedMeeting);
      };
      messageDialogService.openChange(false, false, true, handleChange, onCancelDragging);
    } else {
      meetingViewModel.updateMeeting(actualMeeting).then(() => {
        calendarViewModel.moveMeetingToOtherDay(actualMeeting, meeting.startDate);
        calendarViewModel.setMeetingDragging(meeting.id, false, meeting.startDate);
        meetingViewModel.setMeetingDraggingId(null);
      });
    }
  };

  const onDraggingEnd = (startDate: Date, endDate: Date, e: MouseEvent<HTMLDivElement>) => {
    const date = new Date();
    const newDate = calendarViewModel.viewCalendar[Math.floor((e.clientX - pageLeft) / colWidth)];
    const newStartDate = set(newDate, { hours: startDate.getHours(), minutes: startDate.getMinutes() });
    const newEndDate = set(newDate, { hours: endDate.getHours(), minutes: endDate.getMinutes() });

    if (newStartDate.getTime() > date.getTime()) {
      setIsMeetingInPast(false);
      const layer = getLayer(0,
        newStartDate,
        newEndDate,
        (calendarViewModel.filteredMeetings.get(actualValues.startDate.toDateString()) ?? []).filter(item => item.id !== (meeting.id))
      );

      const updatedValues: IUpdatedValuesWhenDragging = {
        startDate: newStartDate,
        endDate: newEndDate,
        layer
      };
      calendarViewModel.setMeetingDragging(meeting.id, updatedValues, meeting.startDate);
    } else {
      setIsMeetingInPast(true);
      setLeft(getLeft(offsetLeft, isDayCalendar, colWidth, (actualValues.startDate.getDay() === 0 ? 6 : actualValues.startDate.getDay() - 1), actualLayer));
      toast.error('Нельзя перенести встречу в прошедшее время');
    }
  };

  return (
    <DndBlock
      start={actualValues.startDate}
      end={actualValues.endDate}
      startHour={calendarViewModel.startHour}
      className="meeting fade-in"
      color={meeting.calendarColor}
      isCanMove={isCanMove}
      onChangePosition={onChangePosition}
      onClick={clickMeetingHandler}
      onDraggingEnd={onDraggingEnd}
      isUpdateStatesAfterProps={isMeetingInPast || isCreatingMeeting}
      styles={{
        zIndex,
        width,
        left
      }}
      content={({ startDate, endDate }, _setDate) =>
        <Info
          startDate={startDate}
          endDate={endDate}
          isDraggingMeeting={isDraggingMeeting}
          isShowInitiator={isShowInitiator}
          meeting={meeting}
          title={title}
          width={width}
          onMoveMouse={onMoveMouse}
          onAcceptDragging={onAcceptDragging}
          onCancelDragging={onCancelDragging}
        />
      }
    />
  );
});